import { useState, useEffect, useRef } from 'react';

const getGenreIdFromUrl = () => {
  if (typeof window === 'undefined') return null;
  const match = window.location.hash.match(/[&#]id=(\d+)/);
  return match ? parseInt(match[1], 10) : null;
};

/**
 * Custom hook to manage songs data loading and state
 */
export const useSongsPageData = ({
  isChangeDefaultSongFlow,
  isEditPlaylistFlow,
  isGalleryMode,
  isAssignToContactFlow,
  subscriberGallery,
  selectedGenre,
  selectedGenreSongs,
  catagories,
  getCatalog,
  setSelectedGenre,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [songs, setSongs] = useState([]);
  const processedGenreRef = useRef(null);

  const mapSongs = (items) =>
    items.map((song) => ({
      id: song.productId || song.id,
      description: song.description,
      author: song.author,
      duration: song.duration,
      image: song.image,
      funtonPrevUrl: song.funtonPrevUrl || song?.funtonePrevUrl,
      isDownloadable: true,
      isPlayable: true,
    }));

  // Look up full genre data (including image) from the root catalog folders
  const enrichGenre = (genre) => {
    if (!genre) return genre;
    if (genre.image) return genre; // already has image
    const folder = catagories?.[0]?.folders?.find((f) => f.id === genre.id);
    if (!folder) return genre;
    return { ...genre, image: folder.image, title: genre.title || folder.description, description: genre.description || folder.description };
  };

  // Fetch songs for a genre, with subfolder fallback if the genre has no direct items
  const fetchGenreSongs = async (genreId) => {
    setIsLoading(true);
    try {
      const result = await getCatalog(genreId);
      if (result?.payload?.errorCode === 0) {
        const items = result.payload.results.items || [];
        const folders = result.payload.results.folders || [];

        if (items.length > 0) {
          const fetchedSongs = mapSongs(items);
          setSongs(fetchedSongs);
          setSelectedGenre({ genre: enrichGenre(selectedGenre), songs: fetchedSongs });
        } else if (folders.length > 0) {
          // No direct items — try each subfolder until we find songs
          for (const folder of folders) {
            const folderResult = await getCatalog(folder.id);
            if (folderResult?.payload?.errorCode === 0) {
              const folderItems = folderResult.payload.results.items || [];
              if (folderItems.length > 0) {
                const fetchedSongs = mapSongs(folderItems);
                setSongs(fetchedSongs);
                setSelectedGenre({ genre: enrichGenre(selectedGenre), songs: fetchedSongs });
                break;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching genre songs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Load songs based on current mode
  useEffect(() => {
    console.log('useSongsPageData effect running:', {
      isChangeDefaultSongFlow,
      isEditPlaylistFlow,
      isGalleryMode,
      isAssignToContactFlow,
      subscriberGalleryLength: subscriberGallery?.length,
      selectedGenreId: selectedGenre?.id,
      selectedGenreSongsLength: selectedGenreSongs?.length,
      processedGenreRef: processedGenreRef.current,
    });

    if (isChangeDefaultSongFlow && subscriberGallery) {
      console.log('Setting songs from subscriberGallery (change default)');
      setSongs(subscriberGallery);
      setIsLoading(false);
      return;
    }

    if (isEditPlaylistFlow && subscriberGallery) {
      console.log('Setting songs from subscriberGallery (edit playlist)');
      setSongs(subscriberGallery);
      setIsLoading(false);
      return;
    }

    if (isGalleryMode && subscriberGallery) {
      console.log('Setting songs from subscriberGallery (gallery mode)');
      setSongs(subscriberGallery);
      setIsLoading(false);
      return;
    }

    if (isAssignToContactFlow && subscriberGallery) {
      console.log('Setting songs from subscriberGallery (assign to contact)');
      setSongs(subscriberGallery);
      setIsLoading(false);
      return;
    }

    if (selectedGenre && selectedGenre.id !== processedGenreRef.current) {
      processedGenreRef.current = selectedGenre.id;

      if (selectedGenreSongs?.length > 0) {
        console.log('Setting songs from selectedGenreSongs:', selectedGenreSongs.length);
        setSongs(selectedGenreSongs);
        setIsLoading(false);
        return;
      }

      const cachedCategoryData = catagories?.[selectedGenre.id];
      if (cachedCategoryData?.items?.length > 0) {
        console.log('Setting songs from cached category data');
        const cachedSongs = cachedCategoryData.items.map((song) => ({
          id: song.productId || song.id,
          description: song.description,
          author: song.author,
          duration: song.duration,
          image: song.image,
          funtonPrevUrl: song.funtonPrevUrl || song?.funtonePrevUrl,
          isDownloadable: true,
          isPlayable: true,
        }));
        setSongs(cachedSongs);
        setSelectedGenre({ genre: enrichGenre(selectedGenre), songs: cachedSongs });
        setIsLoading(false);
      } else {
        console.log('Fetching songs for genre:', selectedGenre.id);
        fetchGenreSongs(selectedGenre.id);
      }
    } else if (!selectedGenre) {
      // No selectedGenre in Redux — try reading genre ID from URL (e.g. browser forward)
      const urlGenreId = getGenreIdFromUrl();
      if (urlGenreId && urlGenreId !== processedGenreRef.current) {
        processedGenreRef.current = urlGenreId;
        const folder = catagories?.[0]?.folders?.find((f) => f.id === urlGenreId);
        const restoredGenre = { id: urlGenreId, image: folder?.image, title: folder?.description, description: folder?.description };
        const cachedCategoryData = catagories?.[urlGenreId];
        if (cachedCategoryData?.items?.length > 0) {
          const cachedSongs = cachedCategoryData.items.map((song) => ({
            id: song.productId || song.id,
            description: song.description,
            author: song.author,
            duration: song.duration,
            image: song.image,
            funtonPrevUrl: song.funtonPrevUrl || song?.funtonePrevUrl,
            isDownloadable: true,
            isPlayable: true,
          }));
          setSongs(cachedSongs);
          setSelectedGenre({ genre: restoredGenre, songs: cachedSongs });
          setIsLoading(false);
        } else {
          setSelectedGenre({ genre: restoredGenre, songs: [] });
          fetchGenreSongs(urlGenreId);
        }
      } else {
        setIsLoading(false);
      }
    } else {
      // No matching condition — nothing to load
      setIsLoading(false);
    }
  }, [selectedGenre?.id, selectedGenreSongs, isGalleryMode, isAssignToContactFlow, subscriberGallery]);

  return { songs, isLoading };
};
